import { useState, useRef } from "react";
import { verifyPanApi } from "../../api/verification";
import { VerificationStatus } from "./VerificationStatus";
import { OrganisationAlreadyRegisteredCard } from "./OrganisationAlreadyRegisteredCard";
import { C, font } from "../../styles/tokens";
import { InlineIcon } from "../InlineIcon";

const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;

const PAN_CATEGORY_DESCRIPTIONS = {
  P: { label: "Individual / Proprietorship", isIndividual: true },
  C: { label: "Company", isIndividual: false },
  F: { label: "Partnership Firm / LLP", isIndividual: false },
  H: { label: "HUF (Hindu Undivided Family)", isIndividual: false },
  A: { label: "Association of Persons (AOP)", isIndividual: false },
  T: { label: "Trust", isIndividual: false },
  B: { label: "Body of Individuals (BOI)", isIndividual: false },
  L: { label: "Local Authority", isIndividual: false },
  J: { label: "Artificial Juridical Person", isIndividual: false },
  G: { label: "Government Agency", isIndividual: false },
};

export function PanVerification({
  accountType,
  pan,
  onPanChange,
  onVerificationSuccess,
  defaultEmail = "",
  onSwitchToGst,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [verifiedData, setVerifiedData] = useState(null);
  const [alreadyRegisteredData, setAlreadyRegisteredData] = useState(null);
  const isSubmittingRef = useRef(false);

  const normalizedPan = (pan || "").trim().toUpperCase();
  const isValidPanFormat = PAN_REGEX.test(normalizedPan);

  const categoryChar = normalizedPan.length >= 4 ? normalizedPan.charAt(3) : "";

  const handleVerifyPan = async () => {
    if (!isValidPanFormat || loading || isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setError(null);
    setAlreadyRegisteredData(null);
    setLoading(true);

    try {
      const normalizedAccountType =
        accountType === "ORGANISATION" || accountType === "ORGANISATION_OFFICER"
          ? "ORGANISATION"
          : "ACADEMIC_INSTITUTION";

      const result = await verifyPanApi({
        pan: normalizedPan,
        accountType: normalizedAccountType,
      });

      if (result?.alreadyRegistered) {
        setAlreadyRegisteredData(result);
        return;
      }

      if (result?.verificationToken) {
        setVerifiedData(result);
        if (onVerificationSuccess) {
          onVerificationSuccess({
            verificationToken: result.verificationToken,
            pan: result.pan || normalizedPan,
            category: result.category || categoryChar,
            registeredName: result.registeredName,
            legalName: result.registeredName,
            tradeName: result.registeredName,
            name: result.registeredName,
            nameOnPan: result.registeredName,
            mobile: result.mobile || result.phone || undefined,
            phone: result.phone || result.mobile || undefined,
            requiresManualApproval: true,
          });
        }
      } else {
        throw new Error(
          result?.message ||
            "PAN verification failed to return a verification token.",
        );
      }
    } catch (err) {
      setError(
        err?.message ||
          "PAN verification failed. Please check the PAN number and try again.",
      );
    } finally {
      isSubmittingRef.current = false;
      setLoading(false);
    }
  };

  return (
    <div style={boxStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <span style={{ fontWeight: 600, fontSize: 13, color: "#1e293b" }}>
          PAN Card Identity Verification
        </span>
        <span style={badgeStyle}>
          {verifiedData ? (
            <>
              <InlineIcon name="check" size={11} /> Verified
            </>
          ) : (
            "Direct Verification"
          )}
        </span>
      </div>

      {/* PAN Input & Verify Button */}
      <div style={{ marginTop: 10 }}>
        <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
          <input
            type="text"
            placeholder="e.g. ABCDE1234F"
            value={pan || ""}
            onChange={(e) => {
              onPanChange(e.target.value.toUpperCase());
              if (verifiedData) {
                setVerifiedData(null);
              }
              if (alreadyRegisteredData) {
                setAlreadyRegisteredData(null);
              }
              setError(null);
            }}
            onKeyDown={(e) => {
              if (
                e.key === "Enter" &&
                isValidPanFormat &&
                !loading &&
                !verifiedData
              ) {
                e.preventDefault();
                handleVerifyPan();
              }
            }}
            disabled={loading || Boolean(verifiedData)}
            maxLength={10}
            className="w-full sm:flex-1 px-3 py-2 text-base sm:text-sm border border-slate-300 rounded-md tracking-wider outline-none focus:border-blue-500 uppercase font-mono font-semibold"
          />

          {!verifiedData && !alreadyRegisteredData && (
            <button
              type="button"
              onClick={handleVerifyPan}
              disabled={!isValidPanFormat || loading}
              className="w-full sm:w-auto min-h-[44px] sm:min-h-0 px-4 py-2 text-sm sm:text-[13px] font-semibold text-white rounded-md transition duration-150 flex items-center justify-center shrink-0"
              style={{
                background: isValidPanFormat && !loading ? C.blue : "#94a3b8",
                cursor:
                  isValidPanFormat && !loading ? "pointer" : "not-allowed",
              }}
            >
              {loading ? "Verifying..." : "Verify PAN"}
            </button>
          )}
        </div>

        {/* PAN Checkbox below the PAN input box */}
        {!verifiedData && onSwitchToGst && (
          <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap items-center justify-between gap-2">
            <label
              htmlFor="panToggleInPan"
              className="flex items-center gap-2 select-none text-xs cursor-pointer text-slate-700 hover:text-slate-900 font-medium"
            >
              <input
                type="checkbox"
                id="panToggleInPan"
                checked={true}
                onChange={(e) => {
                  if (!e.target.checked) {
                    onSwitchToGst();
                  }
                }}
                className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
              />
              <span className="font-semibold text-slate-800">
                Verify using PAN Card instead of GSTIN
              </span>
            </label>

            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-200">
              PAN Card Mode
            </span>
          </div>
        )}
      </div>

      {/* Error message callout */}
      {error && <div style={errorBoxStyle}>{error}</div>}

      {/* Already registered organisation join request card */}
      {alreadyRegisteredData && (
        <OrganisationAlreadyRegisteredCard
          data={alreadyRegisteredData}
          defaultEmail={defaultEmail}
          onReset={() => setAlreadyRegisteredData(null)}
        />
      )}

      {/* Verified Status Display */}
      {verifiedData && (
        <div style={{ marginTop: 12 }}>
          <VerificationStatus
            status="VERIFIED"
            type="PAN"
            referenceId={verifiedData.pan || normalizedPan}
            verifiedAt={new Date().toISOString()}
            details={{
              registeredName: verifiedData.registeredName,
              category: verifiedData.category || categoryChar,
              requiresManualApproval: true,
            }}
          />

          {/* Callout: All PAN registrations require admin review after payment */}
          <div style={warningCalloutStyle} data-testid="pan-approval-notice">
            <div
              style={{
                fontWeight: 700,
                fontSize: 12,
                marginBottom: 4,
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <InlineIcon name="info" size={14} />
              <span>Account Review Notice</span>
            </div>
            <p style={{ margin: 0, fontSize: 12, lineHeight: "1.45" }}>
              PAN verified successfully. Since your organisation is registering
              with a PAN card, your account will be placed under administrator
              review after subscription payment before full portal access is
              granted.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

const boxStyle = {
  border: `1px solid ${C.divider || "#cbd5e1"}`,
  borderRadius: 8,
  padding: 16,
  margin: "10px 0 16px",
  background: "#ffffff",
  fontFamily: font,
  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
};

const headerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  flexWrap: "wrap",
  gap: 8,
};

const badgeStyle = {
  fontSize: 11,
  fontWeight: 600,
  background: "#e0f2fe",
  color: "#0369a1",
  padding: "3px 10px",
  borderRadius: 12,
};

const inputStyle = {
  flex: 1,
  width: "100%",
  padding: "9px 12px",
  border: "1px solid #cbd5e1",
  borderRadius: 6,
  fontSize: 14,
  fontFamily: "monospace",
  fontWeight: 600,
  letterSpacing: "1px",
  outline: "none",
  textTransform: "uppercase",
  boxSizing: "border-box",
};

const buttonStyle = {
  padding: "10px 18px",
  color: "#ffffff",
  border: "none",
  borderRadius: 6,
  fontSize: 13,
  fontWeight: 600,
  whiteSpace: "nowrap",
  transition: "all 0.15s ease-in-out",
};

const errorBoxStyle = {
  marginTop: 10,
  padding: "8px 12px",
  background: "#fef2f2",
  border: "1px solid #fecaca",
  borderRadius: 6,
  color: "#b91c1c",
  fontSize: 12,
};

const warningCalloutStyle = {
  marginTop: 10,
  padding: "10px 14px",
  background: "#fefce8",
  border: "1px solid #fde047",
  borderRadius: 6,
  color: "#854d0e",
};
